/**
 * Одна кривая: шапка, редактируемая формула и ползунки под раскрытием.
 *
 * Всё про кривую собрано в её карточке. Общая панель коэффициентов внизу
 * относилась к активной кривой, и при четырёх кривых на плоскости приходилось
 * держать в голове, чья она сейчас.
 *
 * Шапка — имя семейства и его запись буквами (`y = ax + b`): она не меняется
 * и служит подписью, с чем сравнивать. Строка ниже — та же формула числами, и
 * числа в ней редактируются на месте.
 *
 * Ползунки закрыты до нажатия: три ползунка на каждую из четырёх кривых — это
 * панель в два экрана, из которой ученик видит одну кривую. Кнопка раскрытия
 * стоит под формулой, а не в её конце: три коэффициента общего вида и значок
 * не помещаются в строку панели, и формула переносилась посередине.
 *
 * Цвет — не единственный признак: рядом стоят имя и формула. Дальтонизм и
 * чёрно-белая печать оставляют от четырёх слотов два похожих штриха.
 */

import { useState } from "react";
import { ExactField } from "./ExactField";
import { ParameterControl } from "./ParameterControl";
import { ConvertDialog } from "./ConvertDialog";
import { IconEye, IconEyeOff, IconTrash } from "./icons";
import { IconChevron, IconReview } from "../Icons";
import {
  FAMILY_NAMES, parametersOf, parameterValue,
  type FunctionCurve, type ParameterKey, type SceneCurve,
} from "../../lib/function-lab/model";
import {
  curveFeatures, describeCurve, familyShape, formulaTemplate, formulaText,
} from "../../lib/function-lab/math";
import {
  conversionBlocked, conversionOf, conversionTarget,
} from "../../lib/function-lab/conversion";
import type { ExactNumber } from "../../lib/function-lab/numbers";

export function CurveCard({
  entry, active, removable, onSelect, onToggle, onRemove, onReset, onParameter, onConvert,
}: {
  entry: SceneCurve;
  active: boolean;
  /** Последнюю кривую убрать нельзя: пустая плоскость не объясняет ничего. */
  removable: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onRemove: () => void;
  onReset: () => void;
  /** `final` разделяет ведение ползунка и отпускание: шаг истории — второе. */
  onParameter: (key: ParameterKey, value: ExactNumber, final: boolean) => void;
  /** Замена записи целиком: у нового вида другие коэффициенты. */
  onConvert: (curve: FunctionCurve) => void;
}) {
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const name = FAMILY_NAMES[entry.curve.kind];
  const formula = formulaText(entry.curve);
  const features = curveFeatures(entry.curve);
  const target = conversionTarget(entry.curve);
  const blocked = conversionBlocked(entry.curve);
  const conversion = asking ? conversionOf(entry.curve) : null;

  return <li className={`fn-lab__card fn-lab__curve--${entry.slot}`
    + (active ? " fn-lab__card--active" : "")
    + (entry.visible ? "" : " fn-lab__card--hidden")}>
    <div className="fn-lab__card-head">
      <button className="fn-lab__card-pick" aria-pressed={active}
        aria-label={`${name}: ${describeCurve(entry.curve)}`} onClick={onSelect}>
        <span className="fn-lab__card-family">
          <span className="fn-lab__swatch" aria-hidden="true" />{name}
        </span>
        <span className="fn-lab__card-shape" aria-hidden="true">{familyShape(entry.curve.kind)}</span>
      </button>
      <span className="fn-lab__card-tools">
        <button className="fn-lab__icon" aria-pressed={entry.visible}
          aria-label={`${entry.visible ? "Скрыть" : "Показать"} ${formula}`} onClick={onToggle}>
          {entry.visible ? <IconEye /> : <IconEyeOff />}
        </button>
        <button className="fn-lab__icon"
          aria-label={`Сбросить ${formula} к базовым коэффициентам`} onClick={onReset}>
          <IconReview size={18} />
        </button>
        <button className="fn-lab__icon" disabled={!removable}
          aria-label={`Удалить ${formula}`} onClick={onRemove}>
          <IconTrash />
        </button>
      </span>
    </div>

    <div className="fn-lab__card-edit">
      <span className="fn-lab__formula-row">
        {formulaTemplate(entry.curve.kind).map((part, index) => {
          if ("text" in part) {
            return <span key={index} className="fn-lab__formula-text">{part.text}</span>;
          }
          const value = parameterValue(entry.curve, part.param);
          if (value === null) return null;
          const meta = parametersOf(entry.curve.kind).find((m) => m.key === part.param);
          return <ExactField key={index} value={value}
            label={`${meta?.name ?? part.param} (${part.param}), ${name}`}
            onSettle={(next) => onParameter(part.param, next, true)} />;
        })}
      </span>
    </div>

    <div className="fn-lab__card-more">
      {target ? (
        <button className="fn-lab__convert" disabled={blocked !== null}
          title={blocked ?? `Преобразовать в вид ${familyShape(target)}`}
          aria-label={`Преобразовать ${formula} в вид ${familyShape(target)}`}
          onClick={() => setAsking(true)}>
          Другой вид
        </button>
      ) : null}
      <button className={`fn-lab__icon fn-lab__expand${open ? " fn-lab__expand--open" : ""}`}
        aria-expanded={open} aria-label={`Ползунки коэффициентов: ${formula}`}
        onClick={() => setOpen((was) => !was)}>
        <IconChevron size={16} width={2.2} />
      </button>
    </div>

    {features.degenerate ? (
      <p className="fn-lab__degenerate" role="status">{features.degenerate}</p>
    ) : null}

    {open ? <div className="fn-lab__card-params">
      {parametersOf(entry.curve.kind).map((meta) => {
        const value = parameterValue(entry.curve, meta.key);
        return value === null ? null : <ParameterControl key={meta.key} meta={meta} value={value}
          onLive={(next) => onParameter(meta.key, next, false)}
          onSettle={(next) => onParameter(meta.key, next, true)} />;
      })}
    </div> : null}

    {conversion ? <ConvertDialog conversion={conversion}
      onCancel={() => setAsking(false)}
      onConvert={() => {
        setAsking(false);
        // Ползунки нового вида раскрываются сразу: ученик согласился на другую
        // запись ради других коэффициентов, и искать их он не должен.
        setOpen(true);
        onConvert(conversion.result);
      }} /> : null}
  </li>;
}
